import { useSnackbar } from '@components/snackbar';
import { CloseIcon } from '@sketchnote/icons';
import {
  boardSelectDropDownIndicator,
  boardSelectOption,
  boardSelectSingleValue,
  boardSelectStyles,
  Button,
} from '@sketchnote/shared-components';
import cn from 'classnames';
import * as React from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { useBoardStore } from 'src/features/boards';
import { useFileService } from 'src/features/files';

const { useState, useRef, useCallback } = React;

export const MoveBoardItem = ({
  onCancel: onClose,
  selectedItems,
  onAfterMove,
}: {
  onCancel: () => void;
  selectedItems: string[];
  onAfterMove?: (fileId: string) => void;
}) => {
  const [selectedBoardId, setSelectedBoardId] = useState<string>();
  const [selectedBoardName, setSelectedBoardName] = useState<string>();
  const [isBoardSelected, toggleIsBoardSelected] = useState<boolean>(true);
  const boardSelectRef = useRef<any>();
  const [open] = useSnackbar();
  const { handleMoveSelectedItems } = useBoardStore((s) => ({
    handleMoveSelectedItems: s.handleMoveSelectedItems,
  }));
  const [loading, setLoading] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [search, setSearch] = useState('');

  const handleBoardChange = (options) => {
    setSelectedBoardId(options.value);
    setSelectedBoardName(options.label);
    toggleIsBoardSelected(true);
  };

  const loadOptions = useCallback(
    async (value, prevOptions) => {
      setLoading(1);
      let page = pageNum;
      if (value !== search) {
        page = 1;
        setSearch(value);
        setPageNum(1);
      }
      const response = await useFileService().fetchBoardFiles({
        page: page,
        search: value,
      });
      setLoading(-1);
      if (!response) return { options: [], hasMore: false };

      const hasMore =
        response.data.data.totalFiles > response.data.data.files.length + prevOptions.length;
      if (hasMore) {
        setPageNum(page + 1);
      }
      return {
        options: response.data.data.files.map((file) => ({
          label: file.title || 'Untitled',
          value: file._id,
          workspace: file.workspace.name,
        })),
        hasMore,
      };
    },
    [pageNum]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedItems.length && selectedBoardId) {
      const success = await handleMoveSelectedItems(selectedBoardId, selectedItems);

      if (success) {
        open({
          text: `The task${
            selectedItems.length === 1 ? ' has' : 's have'
          } been moved to ${selectedBoardName}`,
          snackType: 'standard',
          position: 'bottom-center',
        });
        onAfterMove?.(selectedBoardId);
      } else {
        open({
          text: `The task${
            selectedItems.length === 1 ? ' has' : 's have'
          } been moved to ${selectedBoardName}`,
          snackType: 'standard',
          position: 'bottom-center',
        });
      }
    }
    onClose();
  };

  const handleClose = (e) => {
    e.preventDefault();
    onClose();
  };

  return (
    <form className="relative" onSubmit={handleSubmit}>
      <button
        aria-label="Close button"
        onClick={handleClose}
        className={cn({
          'text-gray-500 text-xs absolute top-24 right-24': true,
        })}
      >
        <CloseIcon />
      </button>
      <div className="overflow-y-auto p-40" style={{ maxHeight: '80vh' }}>
        <h4 className="text-3xl mb-16">Move tasks</h4>
        <p className="text-base mb-32">
          Choose the task destination. You can move tasks between files
        </p>

        <section className="mb-24">
          <div className="w-full">
            <div className="flex justify-between">
              <label
                htmlFor="assigner"
                className={cn('text-base block font-light text-gray-600', {})}
              >
                File
              </label>
              {!isBoardSelected && <span className="text-xs text-red-800 block">Required *</span>}
            </div>
            <AsyncPaginate
              isLoading={loading === 1}
              loadOptions={loadOptions}
              onChange={handleBoardChange}
              debounceTimeout={500}
              menuPortalTarget={document.body}
              styles={boardSelectStyles}
              menuPlacement="bottom"
              placeholder="Select File"
              className={cn('mt-14', { 'border border-red-400 rounded-md': !isBoardSelected })}
              isSearchable
              ref={boardSelectRef}
              components={{
                DropdownIndicator: boardSelectDropDownIndicator,
                IndicatorSeparator: () => null,
                SingleValue: boardSelectSingleValue,
                Option: boardSelectOption,
              }}
            />
          </div>
        </section>

        <section className="flex justify-end">
          <Button
            status="secondary"
            size="lg"
            onClick={onClose}
            className="justify-center min-w-96 mr-24"
          >
            Cancel
          </Button>
          <Button
            size="lg"
            type="submit"
            onClick={handleSubmit}
            className="justify-center min-w-96"
          >
            Move
          </Button>
        </section>
      </div>
    </form>
  );
};
